import { apiClient } from "@/helpers/apiClient";
import type { RouteTypes } from "@energovizor/api-client";
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  type SerializedError,
} from "@reduxjs/toolkit";

type ConfigPayload = NonNullable<RouteTypes["/config"]["get"]["response"]["payload"]>;

export type ConfigSliceState = {
  loading: boolean;
  error: SerializedError | null;
  data: ConfigPayload | null;
  updated: boolean;
};

type ConfigThunkState = {
  config: ConfigSliceState;
};

const UPDATE_CONFIG = "config/update";
const REFRESH_CONFIG = "config/refresh";

const fetchConfig = async () => {
  const res = await apiClient.get("/config");
  return (res.data.payload ?? null) as ConfigPayload | null;
};

const initialState: ConfigSliceState = {
  loading: false,
  error: null,
  data: null,
  updated: false,
};

export const updateConfig = createAsyncThunk(
  UPDATE_CONFIG,
  async () => fetchConfig(),
  {
    condition: (_, thunkAPI) => {
      const state = thunkAPI.getState() as ConfigThunkState;
      return !state.config.loading && !state.config.updated;
    },
  }
);

export const refreshConfig = createAsyncThunk(
  REFRESH_CONFIG,
  async () => fetchConfig(),
  {
    condition: (_, thunkAPI) => {
      const state = thunkAPI.getState() as ConfigThunkState;
      return !state.config.loading;
    },
  }
);

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(isAnyOf(updateConfig.pending, refreshConfig.pending), (state) => {
        state.loading = true;
        state.error = null;
        state.updated = true;
      })
      .addMatcher(isAnyOf(updateConfig.fulfilled, refreshConfig.fulfilled), (state, action) => {
        state.loading = false;
        state.data = action.payload as ConfigPayload | null;
      })
      .addMatcher(isAnyOf(updateConfig.rejected, refreshConfig.rejected), (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  },
});

export default configSlice.reducer;